import { z } from 'zod';
import { isCalendarDate, normalizeName } from './people.js';

/** Currencies of billing accounts; the exponent catalog lives in `@stm/domain`. */
export const billingCurrencies = ['RON', 'EUR', 'HUF'] as const;
export type BillingCurrency = (typeof billingCurrencies)[number];

export const paymentMethods = [
  'cash',
  'bank_transfer',
  'card_terminal',
  'opening_balance',
  'other',
] as const;
export type PaymentMethod = (typeof paymentMethods)[number];

export const billingAccessPermissions = ['view', 'manage'] as const;
export type BillingAccessPermission = (typeof billingAccessPermissions)[number];

export const chargeKinds = ['fee', 'manual', 'opening'] as const;
export type ChargeKind = (typeof chargeKinds)[number];

const calendarDateSchema = z.string().refine(isCalendarDate);
const reasonSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(500));
const referenceSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));
const optionalReferenceSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().max(200))
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .default(null);

/** A positive amount in minor units as a decimal integer string (JSON never carries a float). */
export const minorUnitsSchema = z.string().regex(/^[1-9]\d{0,17}$/);

export const createBillingAccountRequestSchema = z.strictObject({
  label: z.string().transform(normalizeName).pipe(z.string().min(1).max(120)),
  /** Defaults to the club's currency. An account never changes currency. */
  currency: z.enum(billingCurrencies).optional(),
});
export type CreateBillingAccountRequest = z.infer<typeof createBillingAccountRequestSchema>;

/** Puts an athlete on the account from `startsOn` (default: today); an athlete has one default account at a time. */
export const assignAthleteRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    startsOn: calendarDateSchema.optional(),
    endsOn: calendarDateSchema.nullable().default(null),
  })
  .refine(
    (request) =>
      request.startsOn === undefined ||
      request.endsOn === null ||
      request.endsOn >= request.startsOn,
  );
export type AssignAthleteRequest = z.infer<typeof assignAthleteRequestSchema>;

export const endAssignmentRequestSchema = z.strictObject({
  endsOn: calendarDateSchema.optional(),
});
export type EndAssignmentRequest = z.infer<typeof endAssignmentRequestSchema>;

/** Approves a person for one account. This is independent of any guardian link. */
export const grantBillingAccessRequestSchema = z
  .strictObject({
    personId: z.uuid(),
    permission: z.enum(billingAccessPermissions),
    validFrom: calendarDateSchema.optional(),
    validUntil: calendarDateSchema.nullable().default(null),
  })
  .refine(
    (request) =>
      request.validFrom === undefined ||
      request.validUntil === null ||
      request.validUntil >= request.validFrom,
  );
export type GrantBillingAccessRequest = z.infer<typeof grantBillingAccessRequestSchema>;

/** A manual charge. Fee charges come from the fee run and opening balances from the finance import. */
export const postChargeRequestSchema = z.strictObject({
  athleteId: z.uuid().nullable().default(null),
  description: z.string().transform(normalizeName).pipe(z.string().min(1).max(200)),
  amountMinor: minorUnitsSchema,
  dueOn: calendarDateSchema,
  sourceReference: optionalReferenceSchema,
});
export type PostChargeRequest = z.infer<typeof postChargeRequestSchema>;

/** A calendar-year obligation of one payer account, posted in full after a preview. */
export const annualDuesRequestSchema = z
  .strictObject({
    year: z.number().int().min(2000).max(2100),
    description: z.string().transform(normalizeName).pipe(z.string().min(1).max(160)),
    amountMinor: minorUnitsSchema,
    installments: z.union([z.literal(1), z.literal(12)]),
    dueDay: z.number().int().min(1).max(31),
    /** Used for a single annual charge; monthly installments always cover January–December. */
    dueMonth: z.number().int().min(1).max(12).default(1),
  })
  .refine(
    (request) =>
      /^[1-9]\d{0,17}$/.test(request.amountMinor) &&
      BigInt(request.amountMinor) >= BigInt(request.installments),
    {
      path: ['amountMinor'],
    },
  );
export type AnnualDuesRequest = z.infer<typeof annualDuesRequestSchema>;

export const annualDuesQuerySchema = z.strictObject({
  year: z.coerce.number().int().min(2000).max(2100),
});

export interface AnnualDuesPreviewView {
  year: number;
  amountMinor: string;
  currency: string;
  installments: { billingPeriod: string; dueOn: string; amountMinor: string }[];
}

export interface AnnualDuesView {
  year: number;
  currency: string;
  /** Original obligation, credits and active allocations apply to this year's dues only. */
  amountMinor: string;
  creditedMinor: string;
  paidMinor: string;
  remainingMinor: string;
  charges: ChargeView[];
  /** Payments that currently fund these dues, including payments made in a different year. */
  payments: (PaymentView & { appliedMinor: string })[];
}

/** The yearly fee of one age category for one calendar year; zero is a category that pays nothing. */
export const setAnnualFeeRateRequestSchema = z.strictObject({
  year: z.number().int().min(2000).max(2100),
  sportCode: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  categoryCode: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_]{0,31}$/),
  amountMinor: z.string().regex(/^(0|[1-9]\d{0,17})$/),
});
export type SetAnnualFeeRateRequest = z.infer<typeof setAnnualFeeRateRequestSchema>;

export interface AnnualFeeRateView {
  sportCode: string;
  categoryCode: string;
  /** Null while no rate is set for the category and year. */
  amountMinor: string | null;
  currency: string;
  version: number | null;
}

export interface AnnualFeeRatesView {
  year: number;
  /** The club's currency; new rates are set in it. */
  currency: string;
  /** One line per enabled age category of the club's sports, set or not. */
  rates: AnnualFeeRateView[];
}

export interface AnnualFeeTeamView {
  teamSeasonId: string;
  teamName: string;
  sportCode: string;
  categoryCode: string;
}

export interface AnnualFeeAthleteView {
  athleteId: string;
  givenName: string;
  familyName: string;
  /** The team at the start of the year, or the first one joined during it; null when on no team. */
  team: AnnualFeeTeamView | null;
  /** Null when there is no team or no rate for its category in the account's currency. */
  feeMinor: string | null;
}

export interface AnnualFeeAccountView {
  accountId: string;
  label: string;
  currency: string;
  status: string;
  athletes: AnnualFeeAthleteView[];
  feeMinor: string;
  /** Payments dated in the year, net of refunds, without reversed payments. */
  paidMinor: string;
  remainingMinor: string;
  overpaidMinor: string;
  /** False when an athlete has no team or no rate, or the account has no athlete. */
  complete: boolean;
}

export interface AnnualFeeOverviewView {
  year: number;
  accounts: AnnualFeeAccountView[];
}

export const postCreditNoteRequestSchema = z.strictObject({
  amountMinor: minorUnitsSchema,
  reason: reasonSchema,
});
export type PostCreditNoteRequest = z.infer<typeof postCreditNoteRequestSchema>;

const allocationLineSchema = z.strictObject({
  chargeId: z.uuid(),
  amountMinor: minorUnitsSchema,
});
export type AllocationLineRequest = z.infer<typeof allocationLineSchema>;

/**
 * Posts a payment and confirms how it is allocated. An empty allocation list keeps the whole amount
 * as unallocated credit. The currency must be the account's currency.
 */
export const postPaymentRequestSchema = z.strictObject({
  accountId: z.uuid(),
  amountMinor: minorUnitsSchema,
  currency: z.enum(billingCurrencies),
  paidOn: calendarDateSchema,
  method: z.enum(paymentMethods),
  payerReference: optionalReferenceSchema,
  reference: referenceSchema,
  allocations: z.array(allocationLineSchema).max(200),
});
export type PostPaymentRequest = z.infer<typeof postPaymentRequestSchema>;

export const allocatePaymentRequestSchema = z.strictObject({
  allocations: z.array(allocationLineSchema).min(1).max(200),
});
export type AllocatePaymentRequest = z.infer<typeof allocatePaymentRequestSchema>;

export const reverseEntryRequestSchema = z.strictObject({ reason: reasonSchema });
export type ReverseEntryRequest = z.infer<typeof reverseEntryRequestSchema>;

export const postRefundRequestSchema = z.strictObject({
  amountMinor: minorUnitsSchema,
  reason: reasonSchema,
});
export type PostRefundRequest = z.infer<typeof postRefundRequestSchema>;

/** The allocation preview of an amount against the account's open charges, oldest due first. */
export const allocationPreviewQuerySchema = z.strictObject({
  amountMinor: minorUnitsSchema,
});

/**
 * The statement of an account as the ledger stood at the end of a club-local day (default: today).
 * Entries count by when the ledger recorded them, so a statement can be reproduced later.
 */
export const statementQuerySchema = z.strictObject({
  asOf: calendarDateSchema.refine((day) => day >= '2000-01-01' && day <= '2100-12-31').optional(),
});
export type StatementQuery = z.infer<typeof statementQuerySchema>;

/** Looks athletes up by name to assign them to an account; at least two characters. */
export const billingAthleteSearchQuerySchema = z.strictObject({
  search: z.string().transform(normalizeName).pipe(z.string().min(2).max(100)),
});

export interface BillingAthleteMatchView {
  id: string;
  givenName: string;
  familyName: string;
  birthYear: number;
}

/** Looks people up by name to approve them for an account; at least two characters. */
export const billingPersonSearchQuerySchema = billingAthleteSearchQuerySchema;

export interface BillingPersonMatchView {
  id: string;
  givenName: string;
  familyName: string;
  /** Present only when the person is also an athlete. */
  birthYear: number | null;
}

/** Account position (specification section 12); every amount is a decimal string of minor units. */
export interface AccountPositionView {
  /** Net posted charges minus net retained payments; negative means credit. */
  balanceMinor: string;
  overdueMinor: string;
  upcomingMinor: string;
  unallocatedCreditMinor: string;
}

export interface BillingAccountSummaryView {
  id: string;
  label: string;
  currency: string;
  status: 'active' | 'closed';
  version: number;
  position: AccountPositionView;
}

/**
 * An account a person was explicitly approved for (`BillingAccess`), as that person sees it. It never
 * carries the athletes assigned to the account or the people who share it, and a guardian link does
 * not add anything here.
 */
export interface FamilyAccountView {
  id: string;
  label: string;
  currency: string;
  status: 'active' | 'closed';
  permission: BillingAccessPermission;
  position: AccountPositionView;
}

export interface FamilyAccountsView {
  items: FamilyAccountView[];
}

export interface BillingAccountsPage {
  items: BillingAccountSummaryView[];
  nextCursor: string | null;
}

export interface BillingAssignmentView {
  id: string;
  athleteId: string;
  athleteName: string;
  startsOn: string;
  endsOn: string | null;
  version: number;
}

export interface BillingAccessView {
  id: string;
  personId: string;
  personName: string;
  permission: BillingAccessPermission;
  validFrom: string;
  validUntil: string | null;
  revoked: boolean;
  version: number;
}

export interface ChargeView {
  id: string;
  kind: ChargeKind;
  athleteId: string | null;
  billingPeriod: string | null;
  description: string;
  amountMinor: string;
  dueOn: string;
  creditedMinor: string;
  allocatedMinor: string;
  outstandingMinor: string;
  /** True once a reversal voided the charge. */
  reversed: boolean;
  postedAt: string;
}

export interface PaymentView {
  id: string;
  method: PaymentMethod;
  paidOn: string;
  payerReference: string | null;
  reference: string;
  amountMinor: string;
  allocatedMinor: string;
  refundedMinor: string;
  /** Retained value that is neither allocated nor refunded; zero for a reversed payment. */
  availableMinor: string;
  reversed: boolean;
  postedAt: string;
}

export interface AllocationView {
  id: string;
  paymentId: string;
  chargeId: string;
  entryType: 'allocation' | 'reversal';
  reversesId: string | null;
  amountMinor: string;
  reason: string | null;
  /** True for an allocation that a later reversal cancelled. */
  reversed: boolean;
  postedAt: string;
}

/** The newest entries of the account; the position is computed over every entry. */
export interface BillingAccountDetailView extends BillingAccountSummaryView {
  assignments: BillingAssignmentView[];
  access: BillingAccessView[];
  charges: ChargeView[];
  payments: PaymentView[];
  allocations: AllocationView[];
}

export const statementEntryKinds = [
  'charge',
  'credit_note',
  'payment',
  'payment_reversal',
  'refund',
] as const;
export type StatementEntryKindName = (typeof statementEntryKinds)[number];

export interface StatementLineView {
  id: string;
  kind: StatementEntryKindName;
  postedAt: string;
  /** The club-local day the ledger recorded the entry. */
  postedOn: string;
  /** Due date of a charge or paid date of a payment; null for the other kinds. */
  effectiveOn: string | null;
  /** Charge description or payment reference; null for the other kinds. */
  description: string | null;
  /** Signed effect on the balance (negative lowers what is owed). */
  effectMinor: string;
  /** Balance after this line. */
  balanceMinor: string;
}

export interface StatementView {
  accountId: string;
  label: string;
  currency: string;
  asOf: string;
  /** Overdue, upcoming, unallocated credit and balance as they stood on `asOf`. */
  position: AccountPositionView;
  openingBalanceMinor: string;
  closingBalanceMinor: string;
  /** True when older lines are folded into the opening balance. */
  truncated: boolean;
  lines: StatementLineView[];
  /** Charges with an amount still to pay on `asOf`, oldest due first. */
  openCharges: ChargeView[];
}

export interface PaymentPostedView {
  paymentId: string;
  status: 'posted';
  version: 1;
  currency: string;
  amountMinor: string;
  allocatedMinor: string;
  unallocatedMinor: string;
  allocations: AllocationView[];
}

export interface AllocationPreviewView {
  lines: { chargeId: string; amountMinor: string }[];
  allocatedMinor: string;
  unallocatedMinor: string;
}

export interface LedgerEntryPostedView {
  id: string;
  /** Position of the account after the entry. */
  position: AccountPositionView;
}

// ---------------------------------------------------------------------------------------------
// Fee plans, fee assignments and the monthly fee run (specification section 12)
// ---------------------------------------------------------------------------------------------

export const feeConcessionKinds = ['none', 'amount', 'percent'] as const;
export type FeeConcessionKind = (typeof feeConcessionKinds)[number];

const planNameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(120));
const optionalPlanNameSchema = z
  .string()
  .transform(normalizeName)
  .pipe(z.string().max(120))
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .default(null);

/**
 * A new fee plan, or the next version of one. The English name is required; a missing Hungarian or
 * Romanian name falls back to it. A version is immutable once created.
 */
export const createFeePlanRequestSchema = z
  .strictObject({
    nameEn: planNameSchema,
    nameHu: optionalPlanNameSchema,
    nameRo: optionalPlanNameSchema,
    amountMinor: minorUnitsSchema,
    currency: z.enum(billingCurrencies),
    /** A due day beyond the end of a month is clamped to the month's last day. */
    dueDay: z.number().int().min(1).max(31),
    effectiveFrom: calendarDateSchema,
    effectiveTo: calendarDateSchema.nullable().default(null),
  })
  .refine(
    (request) => request.effectiveTo === null || request.effectiveTo >= request.effectiveFrom,
  );
export type CreateFeePlanRequest = z.infer<typeof createFeePlanRequestSchema>;

/** An approved concession; `value` is minor units for `amount` and basis points for `percent`. */
export const feeConcessionSchema = z
  .strictObject({
    kind: z.enum(feeConcessionKinds),
    value: z.string().regex(/^(0|[1-9]\d{0,17})$/),
    reason: reasonSchema.nullable().default(null),
  })
  .refine((concession) => {
    if (concession.kind === 'none') return concession.value === '0' && concession.reason === null;
    if (concession.reason === null || concession.value === '0') return false;
    return concession.kind === 'amount' || Number(concession.value) <= 10000;
  });
export type FeeConcessionRequest = z.infer<typeof feeConcessionSchema>;

export const createFeeAssignmentRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    planVersionId: z.uuid(),
    accountId: z.uuid(),
    startsOn: calendarDateSchema.optional(),
    endsOn: calendarDateSchema.nullable().default(null),
    concession: feeConcessionSchema.default({ kind: 'none', value: '0', reason: null }),
  })
  .refine(
    (request) =>
      request.startsOn === undefined ||
      request.endsOn === null ||
      request.endsOn >= request.startsOn,
  );
export type CreateFeeAssignmentRequest = z.infer<typeof createFeeAssignmentRequestSchema>;

export const endFeeAssignmentRequestSchema = z.strictObject({
  endsOn: calendarDateSchema.optional(),
});
export type EndFeeAssignmentRequest = z.infer<typeof endFeeAssignmentRequestSchema>;

/** Pauses billing from a date (inclusive) to another (inclusive, or open); `pausedFrom: null` resumes. */
export const pauseFeeAssignmentRequestSchema = z
  .strictObject({
    pausedFrom: calendarDateSchema.nullable(),
    pausedUntil: calendarDateSchema.nullable().default(null),
  })
  .refine((request) =>
    request.pausedFrom === null
      ? request.pausedUntil === null
      : request.pausedUntil === null || request.pausedUntil >= request.pausedFrom,
  );
export type PauseFeeAssignmentRequest = z.infer<typeof pauseFeeAssignmentRequestSchema>;

const feeRunPeriodShape = {
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
};

export const feeRunPreviewRequestSchema = z.strictObject(feeRunPeriodShape);
export type FeeRunPreviewRequest = z.infer<typeof feeRunPreviewRequestSchema>;

/**
 * Commits the run for a month. A mid-month joiner is posted only when finance names the reviewed
 * amount here; every other row follows the policy and cannot be overridden.
 */
export const commitFeeRunRequestSchema = z.strictObject({
  ...feeRunPeriodShape,
  reviewed: z
    .array(z.strictObject({ feeAssignmentId: z.uuid(), amountMinor: minorUnitsSchema }))
    .max(500)
    .default([]),
});
export type CommitFeeRunRequest = z.infer<typeof commitFeeRunRequestSchema>;

/**
 * Switches the automatic monthly fee run for the club on or off. It is off until finance turns it on;
 * the worker then posts each current month's charges, and mid-month joiners still wait for review.
 */
export const updateFeeRunScheduleRequestSchema = z.strictObject({ automatic: z.boolean() });
export type UpdateFeeRunScheduleRequest = z.infer<typeof updateFeeRunScheduleRequestSchema>;

export interface ScheduledFeeRunView {
  /** The club-local day the run belongs to. */
  localDay: string;
  billingPeriod: string;
  status: 'succeeded' | 'failed';
  createdCount: number | null;
  awaitingReviewCount: number | null;
  attempts: number;
  finishedAt: string;
}

export interface FeeRunScheduleView {
  automatic: boolean;
  /** The latest automatic run that finished, successfully or not; null before the first. */
  lastRun: ScheduledFeeRunView | null;
}

export interface FeePlanVersionView {
  id: string;
  planId: string;
  versionNumber: number;
  nameEn: string;
  nameHu: string | null;
  nameRo: string | null;
  amountMinor: string;
  currency: string;
  dueDay: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface FeePlansView {
  items: FeePlanVersionView[];
  /** The club's currency (set under Club data); a new plan starts in it. */
  clubCurrency: string;
}

export interface FeeAssignmentView {
  id: string;
  athleteId: string;
  athleteName: string;
  planVersionId: string;
  planName: string;
  accountId: string;
  accountLabel: string;
  currency: string;
  startsOn: string;
  endsOn: string | null;
  pausedFrom: string | null;
  pausedUntil: string | null;
  concession: { kind: FeeConcessionKind; value: string; reason: string | null };
  version: number;
}

export interface FeeAssignmentsView {
  items: FeeAssignmentView[];
}

export const feeRunSkipReasons = [
  'not_started',
  'ended',
  'paused',
  'plan_not_effective',
  'fully_waived',
  'account_closed',
  'period_closed',
] as const;
export type FeeRunSkipReason = (typeof feeRunSkipReasons)[number];

export const feeRunOutcomes = [
  'generate',
  'review_first_charge',
  'already_billed',
  'skip',
] as const;
export type FeeRunOutcome = (typeof feeRunOutcomes)[number];

export interface FeeRunRowView {
  feeAssignmentId: string;
  athleteId: string;
  athleteName: string;
  accountId: string;
  accountLabel: string;
  planName: string;
  currency: string;
  dueOn: string;
  /** The plan amount before the concession. */
  baseAmountMinor: string;
  /** The amount that would post (after the concession); "0" when nothing posts. */
  amountMinor: string;
  outcome: FeeRunOutcome;
  skipReason: FeeRunSkipReason | null;
}

export interface FeeRunPreviewView {
  billingPeriod: string;
  rows: FeeRunRowView[];
  generateCount: number;
  reviewCount: number;
  alreadyBilledCount: number;
  skippedCount: number;
  /** What the run would post per currency; accounts of different currencies are never summed. */
  totals: { currency: string; amountMinor: string }[];
}

export interface FeeRunCommittedView {
  billingPeriod: string;
  createdCount: number;
  alreadyBilledCount: number;
  skippedCount: number;
  /** Mid-month joiners still waiting for a reviewed amount. */
  awaitingReviewCount: number;
}

/**
 * The finance report as the ledger stood at the end of a club-local day (default: today). `seasonId`
 * keeps charges due, and payments paid, within that season's dates. `teamSeasonId` keeps charges whose
 * athlete was on that team on the due date; payments have no team, so the payment side is left out.
 * `method` keeps payments of that method, so the charge side is left out; the two cannot be combined.
 */
export const ledgerReportQuerySchema = z
  .strictObject({
    asOf: calendarDateSchema.refine((day) => day >= '2000-01-01' && day <= '2100-12-31').optional(),
    seasonId: z.uuid().optional(),
    teamSeasonId: z.uuid().optional(),
    method: z.enum(paymentMethods).optional(),
    accountId: z.uuid().optional(),
  })
  .refine((query) => query.teamSeasonId === undefined || query.method === undefined);
export type LedgerReportQuery = z.infer<typeof ledgerReportQuerySchema>;

export interface ReportChargeTotalsView {
  count: number;
  chargedMinor: string;
  creditedMinor: string;
  allocatedMinor: string;
  outstandingMinor: string;
  overdueMinor: string;
  upcomingMinor: string;
}

export interface ReportPaymentTotalsView {
  count: number;
  receivedMinor: string;
  reversedMinor: string;
  allocatedMinor: string;
  refundedMinor: string;
  unallocatedCreditMinor: string;
}

export const reconciliationCheckNames = [
  'balance_matches_statements',
  'charges_settle_exactly',
  'allocations_match',
  'payments_accounted',
] as const;
export type ReconciliationCheckName = (typeof reconciliationCheckNames)[number];

export interface ReconciliationCheckView {
  code: ReconciliationCheckName;
  expectedMinor: string;
  actualMinor: string;
  ok: boolean;
}

export interface ReportTeamRowView {
  /** Null for charges whose athlete was on no team on the due date (or has no athlete). */
  teamSeasonId: string | null;
  teamName: string | null;
  totals: ReportChargeTotalsView;
}

export interface ReportMethodRowView {
  method: PaymentMethod;
  totals: ReportPaymentTotalsView;
}

/** One currency of the report; accounts of different currencies are never summed. */
export interface LedgerReportCurrencyView {
  currency: string;
  accountCount: number;
  /** Null when a payment-method filter leaves the charge side out. */
  charges: { totals: ReportChargeTotalsView; byTeam: ReportTeamRowView[] } | null;
  /** Null when a team filter leaves the payment side out. */
  payments: { totals: ReportPaymentTotalsView; byMethod: ReportMethodRowView[] } | null;
  /** Over every entry of the accounts in scope, whatever season, team or method filter applies. */
  reconciliation: ReconciliationCheckView[];
}

export interface LedgerReportView {
  asOf: string;
  generatedAt: string;
  filters: {
    seasonId: string | null;
    teamSeasonId: string | null;
    method: PaymentMethod | null;
    accountId: string | null;
  };
  currencies: LedgerReportCurrencyView[];
}

/** The seasons and teams finance can filter a report by, without access to the team directory. */
export interface LedgerReportFiltersView {
  seasons: { id: string; label: string; startsOn: string; endsOn: string }[];
  teams: { id: string; seasonId: string; name: string }[];
}

/**
 * Closes the ledger through a day: nothing may then be charged as due, or paid, on or before it.
 * A close is final; corrections post later as compensating entries.
 */
export const closePeriodRequestSchema = z.strictObject({
  closedThrough: calendarDateSchema,
  note: reasonSchema,
});
export type ClosePeriodRequest = z.infer<typeof closePeriodRequestSchema>;

export interface PeriodCloseView {
  id: string;
  closedThrough: string;
  note: string;
  closedAt: string;
}

export interface BillingPeriodsView {
  /** The club's current day; a close must lie strictly before it. */
  today: string;
  /** The last closed day, or null when no period has been closed. */
  closedThrough: string | null;
  /** Newest first, at most the last 24. */
  closes: PeriodCloseView[];
}
